import { useEffect, useRef } from 'react'
import styles from "./VMainBannerDefault.module.css"

const VMainBannerDefault = () => {
  const bannerRef = useRef(null)
  const contentRef = useRef(null)
  const bubblesContainerRef = useRef(null)

  useEffect(() => {
    // Определяем количество пузырьков в зависимости от размера экрана
    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth < 1024;
    
    // Создаём анимированные пузырьки воды
    const createBubbles = () => {
      const container = bubblesContainerRef.current
      if (!container) return

      // Меньше пузырьков на мобильных для производительности
      const bubbleCount = isMobile ? 12 : isTablet ? 18 : 25
      
      for (let i = 0; i < bubbleCount; i++) {
        const bubble = document.createElement('div')
        bubble.className = styles.bubble
        
        const size = Math.random() * 30 + 10
        const left = Math.random() * 100
        const animationDuration = Math.random() * 8 + 8
        const animationDelay = Math.random() * 8
        const opacity = Math.random() * 0.3 + 0.2
        
        bubble.style.cssText = `
          left: ${left}%;
          width: ${size}px;
          height: ${size}px;
          animation-duration: ${animationDuration}s;
          animation-delay: ${animationDelay}s;
          opacity: ${opacity};
        `
        
        container.appendChild(bubble)
      }
    }

    // Создаём блики на воде
    const createWaterGlares = () => {
      if (isMobile) return; // Не создаём на мобильных
      
      const container = bubblesContainerRef.current
      if (!container) return

      const glareCount = isTablet ? 3 : 5
      
      for (let i = 0; i < glareCount; i++) {
        const glare = document.createElement('div')
        glare.className = styles.waterGlare
        
        const size = Math.random() * 100 + 80
        const left = Math.random() * 90
        const top = Math.random() * 90
        const animationDuration = Math.random() * 6 + 6
        const animationDelay = Math.random() * 6
        
        glare.style.cssText = `
          left: ${left}%;
          top: ${top}%;
          width: ${size}px;
          height: ${size}px;
          animation-duration: ${animationDuration}s;
          animation-delay: ${animationDelay}s;
        `
        
        container.appendChild(glare)
      }
    }

    createBubbles()
    createWaterGlares()

    // Intersection Observer - пауза анимаций когда баннер не виден
    const banner = bannerRef.current
    const container = bubblesContainerRef.current
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (container) {
          container.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          // Паузим все дочерние анимации
          const children = container.querySelectorAll('*');
          children.forEach(child => {
            child.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          });
        }
      },
      { threshold: 0.1 }
    );

    if (banner) {
      observer.observe(banner);
    }

    // Анимация появления контента
    const content = contentRef.current
    if (content) {
      const elements = content.querySelectorAll('[data-animate]')
      elements.forEach((el, index) => {
        el.style.animationDelay = `${0.3 + index * 0.15}s`
      })
    }

    return () => {
      if (banner) {
        observer.unobserve(banner);
      }
      const container = bubblesContainerRef.current
      if (container) {
        container.innerHTML = ''
      }
    }
  }, [])

  return (
    <section ref={bannerRef} className={styles.banner}>
      {/* Анимированные пузырьки и блики */}
      <div ref={bubblesContainerRef} className={styles.bubblesContainer}></div>
      
      {/* Декоративные волны на фоне */}
      <div className={styles.waves}>
        <svg className={`${styles.waveSvg} ${styles.wave1}`} viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,60 C300,100 900,20 1200,60 L1200,120 L0,120 Z" fill="currentColor" opacity="0.1"/>
        </svg>
        <svg className={`${styles.waveSvg} ${styles.wave2}`} viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,40 C400,80 800,0 1200,40 L1200,120 L0,120 Z" fill="currentColor" opacity="0.08"/>
        </svg>
        <svg className={`${styles.waveSvg} ${styles.wave3}`} viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,80 C200,110 1000,50 1200,80 L1200,120 L0,120 Z" fill="currentColor" opacity="0.05"/>
        </svg>
      </div>

      {/* Градиентный оверлей */}
      <div className={styles.overlay}></div>

      {/* Основной контент */}
      <div ref={contentRef} className={styles.content}>
        {/* Логотип и слоган */}
        <div className={styles.logoBlock} data-animate>
          <svg className={styles.logo} viewBox="0 0 86 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="45.6462" cy="4.0285" rx="1.70672" ry="1.72576" fill="url(#paint0_linear_logo)"/>
            <ellipse cx="40.8513" cy="4.0285" rx="1.70672" ry="1.72576" fill="#0F88C8"/>
            <ellipse cx="50.4411" cy="4.0285" rx="1.70672" ry="1.72576" fill="url(#paint1_linear_logo)"/>
            <ellipse cx="55.2365" cy="4.0285" rx="1.70672" ry="1.72576" fill="url(#paint2_linear_logo)"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M31.8932 0.0879129C31.6245 0.153116 31.2925 0.366823 31.1554 0.562965C30.9787 0.815794 30.925 4.47516 30.971 13.1738L31.0332 24.9705C31.0348 25.2669 31.1379 25.5537 31.3255 25.7833L31.7781 26.3371C32.1866 26.8369 32.9049 27.3825 33.3744 27.5494C34.9485 28.1088 37.5771 27.7908 38.2808 26.9559C38.5849 26.5953 38.5796 26.4236 38.2307 25.3397C37.8027 24.01 37.7601 23.9877 36.5008 24.4353C35.8518 24.6661 35.4922 24.6679 35.075 24.4423C34.731 24.2563 34.5159 23.8976 34.514 23.5066L34.4588 12.0912L34.4005 0.0416053L33.391 0.00541082C32.8358 -0.0145494 32.1617 0.0224435 31.8932 0.0879129ZM7.8574 2.04428C5.22701 2.49857 2.24286 5.05321 1.18115 7.75954C0.183591 10.3025 -0.0990539 12.2602 0.0288093 15.7436C0.194807 20.2663 0.909046 22.5152 2.92973 24.8776C4.71354 26.9634 7.12813 28.1061 9.77019 28.1149C12.2184 28.1232 15.221 26.9181 15.221 25.9276C15.221 24.9607 14.7064 23.0155 14.4154 22.8832C14.2537 22.8095 13.7882 23.0474 13.3813 23.412C11.8512 24.7821 9.06694 25.0823 7.36434 24.0603C6.23197 23.3806 5.03656 21.6609 4.67742 20.1947C4.22586 18.3512 4.03811 15.0067 4.25592 12.683C4.71443 7.78695 6.61219 5.23125 9.78858 5.23125C11.4584 5.23125 12.5491 5.76778 13.6922 7.15222C14.2703 7.85215 14.9045 8.42488 15.1016 8.42488C15.8157 8.42488 17.6885 7.09926 17.6885 6.59386C17.6885 5.96499 15.7946 3.85666 14.5561 3.10696C13.4652 2.44667 10.9941 1.76723 9.83726 1.80928C9.40544 1.82498 8.51444 1.93064 7.8574 2.04428ZM76.3095 8.17045C73.7754 8.765 71.8256 10.827 70.9734 13.8136C70.1999 16.525 70.3399 20.6687 71.2915 23.2279C73.4844 29.1254 81.2987 29.84 84.3245 24.4199C85.2974 22.6775 85.6579 20.9442 85.6579 18.0092C85.6579 12.5322 83.5255 8.99707 79.7282 8.17923C78.2299 7.85668 77.6543 7.85508 76.3095 8.17045ZM21.8385 8.32268C20.8645 8.48423 19.2754 8.98083 18.3054 9.42661C17.6275 9.73826 17.4644 9.94983 17.4651 10.5164C17.466 11.2326 18.215 12.9492 18.5264 12.9489C18.6208 12.9489 19.2027 12.7216 19.8196 12.4438C22.1514 11.3934 24.7762 11.4865 25.2946 12.6378C25.4785 13.0461 25.6207 14.2775 25.5635 14.9638C25.5505 15.1203 24.6187 15.3939 23.4931 15.5719C18.6127 16.3427 16.5256 18.295 16.4756 22.1359C16.4287 25.7303 18.339 27.8557 21.6126 27.8512C23.0621 27.8493 24.273 27.4419 25.3556 26.5921C25.8321 26.2179 26.2524 25.9595 26.2899 26.0175C26.3274 26.0755 26.5847 26.5123 26.8615 26.9878C27.1383 27.4634 27.5472 27.8528 27.77 27.8528C28.2532 27.8528 30.2505 26.57 30.2505 26.2597C30.2505 26.1402 29.999 25.4019 29.6919 24.619C29.1547 23.25 29.1329 22.986 29.1262 17.7396C29.1199 12.999 29.0643 12.1409 28.7016 11.193C28.2572 10.0323 27.4523 9.22116 26.2764 8.74876C25.4386 8.4121 22.8167 8.16034 21.8385 8.32268ZM44.6632 8.96513C44.0155 9.40585 43.4886 9.85376 43.4924 9.96048C43.4962 10.0675 43.7481 10.6338 44.0521 11.2193L44.6048 12.2838L44.606 23.2401C44.6066 29.2662 44.6683 34.3859 44.7432 34.6174C44.9774 35.3413 47.552 34.9589 48.0246 34.1299C48.1152 33.971 48.1908 32.301 48.1929 30.4191C48.196 27.2654 48.227 27.0126 48.5888 27.1898C50.3991 28.0766 53.1587 28.0066 54.8038 27.0318C56.0773 26.2773 56.8155 25.2987 57.5975 23.3285C58.133 21.9794 58.1785 21.5834 58.1785 18.2719C58.1785 14.0869 57.9237 12.773 56.7568 10.9423C54.9804 8.15528 52.0305 7.49686 49.002 9.21157C47.5849 10.014 47.4412 10.0478 47.2415 9.62435C46.9616 9.03087 46.2265 8.15794 46.0091 8.16114C45.9167 8.16273 45.3108 8.52441 44.6632 8.96513ZM62.8502 8.42328C59.6223 9.22674 57.8609 12.7906 59.1781 15.8538C59.8096 17.3221 60.6378 18.0385 63.2118 19.3426C64.4651 19.9776 65.7382 20.769 66.0408 21.1017C66.5823 21.6971 66.7678 22.8683 66.419 23.4882C65.8001 24.5876 63.4429 24.7728 61.3269 23.8879C60.5909 23.5802 59.8185 23.3285 59.6104 23.3285C59.1368 23.3285 58.2449 25.0767 58.3353 25.8283C58.4915 27.1246 63.6513 28.2956 66.1161 27.5941C68.9516 26.7869 70.6277 23.8953 69.9615 20.9596C69.546 19.1291 68.6595 18.2181 65.903 16.7887C63.0236 15.2952 62.3285 14.6538 62.3285 13.4902C62.3285 12.8563 62.4904 12.558 63.0575 12.1478C63.9941 11.4705 65.1264 11.4785 66.8225 12.175C67.7631 12.5612 68.2611 12.6484 68.4497 12.4606C68.9208 11.9911 69.5696 10.3336 69.4395 9.9312C69.1259 8.96194 64.7292 7.95568 62.8502 8.42328ZM36.3521 9.01038C35.9969 9.43167 36.0052 11.5299 36.3633 11.7944C36.5176 11.9083 37.1989 12.0052 37.8774 12.0097L39.1112 12.0177L39.2234 19.6026L39.3355 27.1874H41.0179H42.7003L42.7598 18.2719C42.8141 10.1265 42.7863 9.32761 42.4365 9.02368C41.9172 8.57232 36.7309 8.56087 36.3521 9.01038ZM79.9038 11.4884C81.3498 12.311 82.0656 14.4728 82.0674 18.0239C82.0701 22.7001 80.8069 24.9743 78.1822 25.0195C75.9928 25.057 74.704 23.593 74.2141 20.5128C73.6089 16.7076 74.5466 12.5606 76.24 11.5525C77.201 10.9803 78.9567 10.9494 79.9038 11.4884ZM53.2858 12.2924C54.293 13.0753 54.5681 14.3312 54.5663 18.1388C54.5645 21.9134 54.3619 22.7693 53.2177 23.8373C52.468 24.5367 50.7658 24.5822 49.2617 23.9424L48.1962 23.4895V18.3579V13.2262L49.1496 12.6482C51.0092 11.5211 52.1658 11.4216 53.2858 12.2924ZM25.5768 20.3195C25.618 21.2992 25.5494 22.3438 25.424 22.6405C25.0483 23.5313 22.8836 24.6996 21.8726 24.5578C20.1866 24.3212 19.4449 22.5561 20.2527 20.7025C20.7195 19.6319 22.8163 18.669 24.9038 18.5673C25.487 18.5388 25.5032 18.5811 25.5768 20.3195Z" fill="url(#paint3_linear_logo)"/>
            <defs>
              <linearGradient id="paint0_linear_logo" x1="45.6462" y1="2.30273" x2="45.6462" y2="5.75426" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFD93D"/>
                <stop offset="1" stopColor="#FFB800"/>
              </linearGradient>
              <linearGradient id="paint1_linear_logo" x1="50.4411" y1="2.30273" x2="50.4411" y2="5.75426" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FF4757"/>
                <stop offset="1" stopColor="#C0392B"/>
              </linearGradient>
              <linearGradient id="paint2_linear_logo" x1="55.2365" y1="2.30273" x2="55.2365" y2="5.75426" gradientUnits="userSpaceOnUse">
                <stop stopColor="#A8E063"/>
                <stop offset="1" stopColor="#56AB2F"/>
              </linearGradient>
              {/* <linearGradient id="paint3_linear_logo" x1="42.8289" y1="-0.0546111" x2="42.8289" y2="35" gradientUnits="userSpaceOnUse"> */}
              <linearGradient id="paint3_linear_logo" x1="42.8289" y1="-0.0546111" x2="42.8289" y2="35" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFFFFF"/>
                <stop offset="1" stopColor="#FFFFFF"/>
              </linearGradient>
            </defs>
          </svg>
          <span className={styles.slogan}>Ни дня без Calipso</span>
        </div>

        {/* Заголовок */}
        <h1 className={styles.title} data-animate>
          <span className={styles.titleMain}>Артезианская вода</span>
          <span className={styles.titleAccent}>с глубины 500 метров</span>
        </h1>

        {/* Описание */}
        <p className={styles.description} data-animate>
          Чистейшая природная вода из артезианских скважин Казахстана. 
          Доставляем по всему Алматы ежедневно — закажите прямо сейчас!
        </p>

        {/* Преимущества - карточки с рамками */}
        <div className={styles.features} data-animate>
          <div className={styles.feature}>
            <svg className={styles.featureIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
            </svg>
            <span>100% натуральная</span>
          </div>
          <div className={styles.feature}>
            <svg className={styles.featureIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13"/>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
              <circle cx="5.5" cy="18.5" r="2.5"/>
              <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            <span>Быстрая доставка</span>
          </div>
          <div className={styles.feature}>
            <svg className={styles.featureIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>19л бутыли</span>
          </div>
        </div>

        {/* Продукция - для мобильной версии внутри контента */}
        <img 
          src={require('../../../../assets/img/by_page/main/products.png')} 
          alt="Calipso water products" 
          className={styles.productsMobile}
        />
      </div>

      {/* Продукция - для десктопа */}
      <img 
        src={require('../../../../assets/img/by_page/main/products.png')} 
        alt="Calipso water products" 
        className={styles.products}
      />

      {/* Нижняя декорация */}
      <div className={styles.bottomDecor}>
        <div className={styles.bottomWave}></div>
      </div>
    </section>
  )
}

export default VMainBannerDefault
